import React, { useState, useEffect } from 'react'
import { fetchWithAuth } from '../../utils/apiClient'
import { Transition, Menu } from '@headlessui/react'
import {
	XMarkIcon,
	CheckCircleIcon,
	ChevronDownIcon,
	PaperAirplaneIcon,
} from '@heroicons/react/24/outline'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const formatDate = iso => new Date(iso).toLocaleDateString('en-US')

const connectionDisplayName = conn => {
	if (conn.chatType === 'private') {
		return (
			`${conn.firstName || ''} ${conn.lastName || ''}`.trim() || 'Personal chat'
		)
	}
	return conn.chatTitle || `${conn.chatType} chat`
}

const connectionInitial = conn => {
	const name = connectionDisplayName(conn)
	return name.charAt(0).toUpperCase() || 'T'
}

function TelegramSettings() {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	const [isConnecting, setIsConnecting] = useState(false)
	const [isMaxConnectionsModalOpen, setIsMaxConnectionsModalOpen] = useState(false)
	const [pendingDisconnect, setPendingDisconnect] = useState(null)
	const [disconnectingId, setDisconnectingId] = useState(null)
	const [awaitingConnectReturn, setAwaitingConnectReturn] = useState(false)
	const [connectionStatus, setConnectionStatus] = useState({
		isConnected: false,
		connectionCount: 0,
		maxConnections: 3,
		connections: [],
		loading: true,
	})
	const [notification, setNotification] = useState({
		show: false,
		message: '',
		type: 'error',
	})

	const showNotification = (message, type = 'error') => {
		setNotification({ show: true, message, type })
		setTimeout(
			() => setNotification({ show: false, message: '', type: 'error' }),
			5000,
		)
	}

	useEffect(() => {
		fetchConnectionStatus()
	}, [])

	// Once we've opened the bot in a new tab we don't know when the user
	// finished `/start` over there. The focus event fires once when they
	// switch back — refetch then and detach so we don't keep polling.
	useEffect(() => {
		if (!awaitingConnectReturn) return
		const onFocus = () => {
			fetchConnectionStatus()
			setAwaitingConnectReturn(false)
		}
		window.addEventListener('focus', onFocus)
		return () => window.removeEventListener('focus', onFocus)
	}, [awaitingConnectReturn])

	const fetchConnectionStatus = async () => {
		try {
			const response = await fetchWithAuth('/api/telegram/status')
			const data = await response.json()
			if (data.success) {
				setConnectionStatus({ ...data, loading: false })
			} else {
				setConnectionStatus({
					isConnected: false,
					connectionCount: 0,
					maxConnections: 3,
					connections: [],
					loading: false,
				})
			}
		} catch (error) {
			console.error('Error fetching connection status:', error)
			setConnectionStatus({
				isConnected: false,
				connectionCount: 0,
				maxConnections: 3,
				connections: [],
				loading: false,
			})
		}
	}

	const handleConnect = async (connectionType = 'private') => {
		if (connectionStatus.connectionCount >= connectionStatus.maxConnections) {
			setIsMaxConnectionsModalOpen(true)
			return
		}

		setIsConnecting(true)
		try {
			const response = await fetchWithAuth('/api/telegram/connect-token', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ connectionType }),
			})

			const data = await response.json()

			if (data.success) {
				const botUsername = data.botUsername || 'loadlinebot'
				const telegramUrl =
					connectionType === 'group'
						? `https://t.me/${botUsername}?startgroup=${data.connectToken}`
						: `https://t.me/${botUsername}?start=${data.connectToken}`

				setAwaitingConnectReturn(true)
				window.open(telegramUrl, '_blank')
				showNotification(
					connectionType === 'group'
						? 'Opening Telegram. Select a group to add the bot to.'
						: 'Opening Telegram. Complete the connection in the bot.',
					'success',
				)
			} else {
				showNotification('Failed to generate connection token')
			}
		} catch (error) {
			showNotification('Error connecting to Telegram')
		} finally {
			setIsConnecting(false)
		}
	}

	const confirmDisconnect = async () => {
		if (!pendingDisconnect) return
		const { id } = pendingDisconnect
		setDisconnectingId(id)
		try {
			const response = await fetchWithAuth(`/api/telegram/disconnect/${id}`, {
				method: 'DELETE',
			})
			const data = await response.json()
			if (data.success) {
				showNotification('Telegram connection disconnected.', 'success')
				fetchConnectionStatus()
			} else {
				showNotification(data.message || 'Failed to disconnect Telegram')
			}
		} catch (error) {
			console.error('Error disconnecting Telegram:', error)
			showNotification('Error disconnecting from Telegram')
		} finally {
			setDisconnectingId(null)
			setPendingDisconnect(null)
		}
	}

	return (
		<>
			{/* Notification toast */}
			<div
				aria-live="assertive"
				className="pointer-events-none fixed inset-0 flex items-start justify-center pt-6 z-50"
			>
				<div className="flex w-full flex-col items-center">
					<Transition
						show={notification.show}
						enter="transform ease-out duration-300 transition"
						enterFrom="translate-y-2 opacity-0"
						enterTo="translate-y-0 opacity-100"
						leave="transition ease-in duration-100"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div
							className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg shadow-lg ${
								isLight
									? 'bg-white ring-1 ring-gray-200'
									: 'bg-[#15202b] ring-1 ring-white/10'
							}`}
						>
							<div className="p-4">
								<div className="flex items-start">
									<div className="shrink-0">
										{notification.type === 'success' ? (
											<CheckCircleIcon
												className={`h-6 w-6 ${isLight ? 'text-green-600' : 'text-green-400'}`}
												aria-hidden="true"
											/>
										) : (
											<XMarkIcon
												className={`h-6 w-6 ${isLight ? 'text-red-600' : 'text-red-400'}`}
												aria-hidden="true"
											/>
										)}
									</div>
									<div className="ml-3 w-0 flex-1 pt-0.5">
										<p className={`text-sm font-medium ${isLight ? 'text-gray-900' : 'text-white'}`}>
											{notification.message}
										</p>
									</div>
									<div className="ml-4 flex shrink-0">
										<button
											type="button"
											onClick={() => setNotification({ ...notification, show: false })}
											className={`inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
												isLight
													? 'bg-white text-gray-500 hover:text-gray-700'
													: 'bg-[#15202b] text-gray-400 hover:text-gray-300'
											}`}
										>
											<span className="sr-only">Close</span>
											<XMarkIcon aria-hidden="true" className="h-5 w-5" />
										</button>
									</div>
								</div>
							</div>
						</div>
					</Transition>
				</div>
			</div>

			<div className={isLight ? 'text-gray-900' : 'text-white'}>
				<section className="mb-8">
					<div className="flex justify-between items-center mb-2">
						<h3 className="text-xl font-semibold">Telegram</h3>
						{!connectionStatus.loading && (
							<Menu as="div" className="relative inline-block text-left">
								<Menu.Button
									disabled={isConnecting}
									className={`bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 px-3 py-1.5 rounded text-sm transition-colors flex items-center space-x-2 ${isLight ? 'text-white' : ''}`}
								>
									<span>
										{isConnecting ? 'Connecting...' : '+ Connect to Telegram'}
									</span>
									<ChevronDownIcon className="h-4 w-4" />
								</Menu.Button>

								<Transition
									enter="transition ease-out duration-100"
									enterFrom="transform opacity-0 scale-95"
									enterTo="transform opacity-100 scale-100"
									leave="transition ease-in duration-75"
									leaveFrom="transform opacity-100 scale-100"
									leaveTo="transform opacity-0 scale-95"
								>
									<Menu.Items
										className={`absolute right-0 mt-2 w-56 origin-top-right divide-y rounded-md shadow-lg focus:outline-none z-10 ${
											isLight
												? 'divide-gray-200 bg-white ring-1 ring-gray-200'
												: 'divide-gray-600 bg-[#1a2634] ring-1 ring-white/10'
										}`}
									>
										<div className="px-1 py-1">
											<Menu.Item>
												{({ active }) => (
													<button
														className={`${
															active ? (isLight ? 'bg-gray-100' : 'bg-gray-600') : ''
														} group flex w-full items-center rounded-md px-2 py-2 text-sm ${
															isLight ? 'text-gray-900' : 'text-gray-100'
														}`}
														onClick={() => handleConnect('private')}
														disabled={isConnecting}
													>
														Connect private chat
													</button>
												)}
											</Menu.Item>
											<Menu.Item>
												{({ active }) => (
													<button
														className={`${
															active ? (isLight ? 'bg-gray-100' : 'bg-gray-600') : ''
														} group flex w-full items-center rounded-md px-2 py-2 text-sm ${
															isLight ? 'text-gray-900' : 'text-gray-100'
														}`}
														onClick={() => handleConnect('group')}
														disabled={isConnecting}
													>
														Connect group / channel
													</button>
												)}
											</Menu.Item>
										</div>
									</Menu.Items>
								</Transition>
							</Menu>
						)}
					</div>
					<p className={`text-sm mb-4 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
						Receive load match alerts and email notifications in Telegram.
					</p>

					{connectionStatus.loading ? (
						<ul className="space-y-3">
							{[1, 2].map(i => (
								<li
									key={i}
									className={`p-4 rounded-lg border ${
										isLight
											? 'bg-gray-50 border-gray-200'
											: 'bg-[#263340] border-[#374151]'
									}`}
								>
									<div className="animate-pulse flex items-center space-x-3">
										<div className={`w-10 h-10 rounded-full ${isLight ? 'bg-gray-200' : 'bg-[#374151]'}`} />
										<div className="space-y-2 flex-1">
											<div className={`h-4 rounded w-40 ${isLight ? 'bg-gray-200' : 'bg-[#374151]'}`} />
											<div className={`h-3 rounded w-28 ${isLight ? 'bg-gray-200' : 'bg-[#374151]'}`} />
										</div>
										<div className={`h-8 rounded w-24 ${isLight ? 'bg-gray-200' : 'bg-[#374151]'}`} />
									</div>
								</li>
							))}
						</ul>
					) : !connectionStatus.isConnected ? (
						<div
							className={`p-4 rounded-lg border text-center ${
								isLight
									? 'bg-gray-50 border-gray-200'
									: 'bg-[#263340] border-[#374151]'
							}`}
						>
							<p className={isLight ? 'text-gray-500' : 'text-gray-400'}>
								No Telegram connections yet.
							</p>
						</div>
					) : (
						<ul className="space-y-3">
							{connectionStatus.connections.map(conn => {
								const displayName = connectionDisplayName(conn)
								const initial = connectionInitial(conn)
								const isDisconnecting = disconnectingId === conn.id
								return (
									<li
										key={conn.id}
										className={`p-4 rounded-lg border transition-all duration-200 ${
											isLight
												? 'bg-gray-50 border-gray-200 hover:border-gray-300'
												: 'bg-[#263340] border-[#374151] hover:border-[#4B5563]'
										}`}
									>
										<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
											<div className="flex items-center space-x-3 min-w-0">
												<div
													className={`w-10 h-10 rounded-full shrink-0 flex items-center justify-center text-white font-semibold ${
														conn.chatType === 'private'
															? 'bg-[#0088cc]'
															: 'bg-indigo-500'
													}`}
													aria-hidden="true"
												>
													{initial}
												</div>
												<div className="min-w-0">
													<div className="flex flex-wrap items-center gap-x-2">
														<span className={`font-medium truncate ${isLight ? 'text-gray-900' : 'text-white'}`}>
															{displayName}
														</span>
														<div className="flex items-center">
															<PaperAirplaneIcon
																className={`w-3.5 h-3.5 mr-1 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}
															/>
															<span className={`text-sm capitalize ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
																{conn.chatType}
															</span>
															<span className={`text-sm ml-2 ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
																• Added {formatDate(conn.connectedAt)}
															</span>
														</div>
													</div>
													<div className="flex items-center space-x-2 mt-1.5 text-sm">
														<div className="w-2 h-2 rounded-full bg-green-500" />
														<span className={isLight ? 'text-green-600' : 'text-green-400'}>
															Active
														</span>
														{conn.username && (
															<>
																<span className={isLight ? 'text-gray-400' : 'text-gray-500'}>
																	·
																</span>
																<span className={isLight ? 'text-gray-500' : 'text-gray-400'}>
																	Linked by @{conn.username}
																</span>
															</>
														)}
													</div>
												</div>
											</div>
											<div className="flex flex-wrap gap-2 shrink-0">
												<button
													onClick={() =>
														setPendingDisconnect({ id: conn.id, displayName })
													}
													disabled={isDisconnecting}
													className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
														isLight
															? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
															: 'text-gray-300 bg-[#374151] hover:bg-[#4B5563]'
													}`}
												>
													{isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
												</button>
											</div>
										</div>
									</li>
								)
							})}
						</ul>
					)}
				</section>
			</div>

			{/* Disconnect confirmation */}
			{pendingDisconnect && (
				<>
					<div
						className="fixed inset-0 bg-black/50 z-40"
						onClick={() => setPendingDisconnect(null)}
					/>
					<div className="fixed inset-0 flex items-center justify-center z-50 p-4 -mt-56">
						<div
							className={`rounded-lg p-6 w-full max-w-md shadow-lg ${
								isLight
									? 'bg-white ring-1 ring-gray-200'
									: 'bg-[#15202b] ring-1 ring-white/10'
							}`}
						>
							<div className="flex justify-between items-start">
								<h3 className={`text-lg font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>
									Disconnect Telegram?
								</h3>
								<button
									onClick={() => setPendingDisconnect(null)}
									className={isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300'}
								>
									<XMarkIcon className="h-5 w-5" />
								</button>
							</div>
							<p className={`mt-3 text-sm ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>
								<span className={`font-medium ${isLight ? 'text-gray-900' : 'text-white'}`}>
									{pendingDisconnect.displayName}
								</span>{' '}
								will stop receiving LoadLine notifications. You can reconnect anytime.
							</p>
							<div className="flex justify-end gap-2 mt-6">
								<button
									onClick={() => setPendingDisconnect(null)}
									className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
										isLight
											? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
											: 'bg-[#263340] text-white hover:bg-[#2c3b4a]'
									}`}
								>
									Cancel
								</button>
								<button
									onClick={confirmDisconnect}
									disabled={disconnectingId === pendingDisconnect.id}
									className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-400 transition-colors"
								>
									{disconnectingId === pendingDisconnect.id ? 'Disconnecting…' : 'Disconnect'}
								</button>
							</div>
						</div>
					</div>
				</>
			)}

			{isMaxConnectionsModalOpen && (
				<>
					<div
						className="fixed inset-0 bg-black/50 z-40"
						onClick={() => setIsMaxConnectionsModalOpen(false)}
					/>
					<div className="fixed inset-0 flex items-center justify-center z-50 p-4 -mt-56">
						<div
							className={`rounded-lg p-6 w-full max-w-md shadow-lg ${
								isLight
									? 'bg-white ring-1 ring-gray-200'
									: 'bg-[#15202b] ring-1 ring-white/10'
							}`}
						>
							<div className="flex justify-between items-start">
								<h3 className={`text-lg font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>
									Maximum connections reached
								</h3>
								<button
									onClick={() => setIsMaxConnectionsModalOpen(false)}
									className={isLight ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-300'}
								>
									<XMarkIcon className="h-5 w-5" />
								</button>
							</div>
							<p className={`mt-3 text-sm ${isLight ? 'text-gray-600' : 'text-gray-300'}`}>
								You can have up to 3 Telegram connections. Disconnect one before adding a new one.
							</p>
							<div className="flex justify-end mt-6">
								<button
									onClick={() => setIsMaxConnectionsModalOpen(false)}
									className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
										isLight
											? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
											: 'bg-[#263340] text-white hover:bg-[#2c3b4a]'
									}`}
								>
									Close
								</button>
							</div>
						</div>
					</div>
				</>
			)}
		</>
	)
}

export default TelegramSettings
